import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alignment,
  decodeImage,
  Fit,
  Layout,
  useRive,
  useViewModelInstanceBoolean,
  useViewModelInstanceEnum,
  useViewModelInstanceNumber,
  useViewModelInstanceString,
  type ViewModelInstance,
} from "@rive-app/react-canvas";
import type { Rive } from "@rive-app/canvas";
import {
  BufferTarget,
  CanvasSource,
  MediaStreamAudioTrackSource,
  Mp4OutputFormat,
  Output,
  QUALITY_HIGH,
  canEncodeAudio,
} from "mediabunny";
import { pagesetter, reactvar } from "../App";
import { PageHeader } from "../components/PageHeader";
import { Viewport } from "../hooks/useWindowSize";
import {
  fetchStaticMapBytes,
  getGoogleMapsApiKey,
  loadGoogleMaps,
} from "../lib/googleMaps";
import "./subpages.css";

type Point = { x: number; y: number };
type LangMode = "english" | "spanish" | "both";
type MediaMode = "image" | "video" | "map";
type VideoPhase = "idle" | "main" | "bumper" | "done";

const RIV_SRC = "/fd_graphic_generator.riv?v=5";
const ARTBOARD = "Artboard 1";
const STATE_MACHINE = "State Machine 1";
const VIDEO_MODE_BOOL = "video_mode_db_bool";
const PREVIEW_BOOL = "preview_db_bool";
const INTRO_TRIGGER = "intro_db_trig";
const OUTRO_TRIGGER = "outro_db_trig";
const END_BUMPER_SRC = "/endbumper.mp4";
const OUTRO_LEAD_SECONDS = 0.5;

const STRING_FIELDS = [
  { path: "location_1_db_str", label: "Location" },
  { path: "location_2_db_str", label: "City/Municipality" },
] as const;

const WEEKDAY_NAMES = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
] as const;

const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const WEEKDAY_PATH = "weekday_db_enum";
const IMAGE_PATH = "graphic_db_img";

const SYNC_NUMBER_PATHS = [
  "x_offset_db_num",
  "y_offset_db_num",
  "scalefac_db_num",
  "num_detained_db_num",
  "month_db_num",
  "hour_db_num",
  "minute_db_num",
  "date_db_num",
  "year_db_num",
] as const;

const SYNC_STRING_PATHS = ["location_1_db_str", "location_2_db_str"] as const;
const SYNC_BOOL_PATHS = [
  "at_least_db_bool",
  "am_db_bool",
  "video_mode_db_bool",
  "preview_db_bool",
] as const;

const SCALE_MIN = 10;
const SCALE_MAX = 400;
const RING_BASE_PX = 54;

function isMobileShareDevice(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod|Android/i.test(ua)) {
    return true;
  }
  // iPadOS desktop UA still has touch.
  return navigator.maxTouchPoints > 1 && /Macintosh/i.test(ua);
}

function triggerAnchorDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Save to Photos/Files via the native share sheet on mobile; otherwise download. */
async function saveBlob(blob: Blob, filename: string): Promise<void> {
  const type =
    blob.type ||
    (filename.toLowerCase().endsWith(".png")
      ? "image/png"
      : filename.toLowerCase().endsWith(".mp4")
        ? "video/mp4"
        : "application/octet-stream");
  const file = new File([blob], filename, { type });
  if (
    isMobileShareDevice() &&
    typeof navigator.share === "function" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [file] })
  ) {
    try {
      await navigator.share({ files: [file] });
      return;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      // Fall through to anchor download.
    }
  }
  triggerAnchorDownload(blob, filename);
}

async function saveFiles(
  files: Array<{ blob: Blob; filename: string }>
): Promise<void> {
  if (!files.length) {
    return;
  }
  const fileObjects = files.map(
    ({ blob, filename }) =>
      new File(
        [blob],
        filename,
        {
          type:
            blob.type ||
            (filename.toLowerCase().endsWith(".png")
              ? "image/png"
              : filename.toLowerCase().endsWith(".mp4")
                ? "video/mp4"
                : "application/octet-stream"),
        }
      )
  );
  if (
    isMobileShareDevice() &&
    typeof navigator.share === "function" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: fileObjects })
  ) {
    try {
      await navigator.share({ files: fileObjects });
      return;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
    }
  }
  // Share one-by-one (iOS often rejects multi-file), then download fallback.
  for (const { blob, filename } of files) {
    await saveBlob(blob, filename);
  }
}

async function saveCanvasPng(
  canvas: HTMLCanvasElement,
  filename: string
): Promise<void> {
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((next) => resolve(next), "image/png");
  });
  if (blob) {
    await saveBlob(blob, filename);
    return;
  }
  const response = await fetch(canvas.toDataURL("image/png"));
  await saveBlob(await response.blob(), filename);
}

function sanitizeFilenamePart(value: string): string {
  const cleaned = value
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9._-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return cleaned || "unknown";
}

function buildExportFilename(
  viewModelInstance: ViewModelInstance | null | undefined,
  language: "ENGLISH" | "SPANISH",
  extension = "png"
): string {
  const year = viewModelInstance?.number("year_db_num")?.value;
  const month = viewModelInstance?.number("month_db_num")?.value;
  const day = viewModelInstance?.number("date_db_num")?.value;
  const municipality =
    viewModelInstance?.string("location_2_db_str")?.value?.trim() || "unknown";

  const datePart =
    year != null && month != null && day != null
      ? `${Math.round(year)}-${pad2(Math.round(month))}-${pad2(Math.round(day))}`
      : "unknown-date";

  return `${datePart}_${sanitizeFilenamePart(municipality)}_${language}.${extension}`;
}

function fireRiveTrigger(
  viewModelInstance: ViewModelInstance | null | undefined,
  path: string
): void {
  viewModelInstance?.trigger(path)?.trigger();
}

function setVideoModeBool(
  viewModelInstance: ViewModelInstance | null | undefined,
  enabled: boolean
): void {
  const prop = viewModelInstance?.boolean(VIDEO_MODE_BOOL);
  if (prop && prop.value !== enabled) {
    prop.value = enabled;
  }
}

function setPreviewBool(
  viewModelInstance: ViewModelInstance | null | undefined,
  enabled: boolean
): void {
  const prop = viewModelInstance?.boolean(PREVIEW_BOOL);
  if (prop && prop.value !== enabled) {
    prop.value = enabled;
  }
}

function readVideoFrame(
  viewModelInstance: ViewModelInstance | null | undefined
): { xOffset: number; yOffset: number; scaleFac: number } {
  return {
    xOffset: viewModelInstance?.number("x_offset_db_num")?.value ?? 0,
    yOffset: viewModelInstance?.number("y_offset_db_num")?.value ?? 0,
    scaleFac: viewModelInstance?.number("scalefac_db_num")?.value ?? 100,
  };
}

/**
 * Draw video covering the Rive artboard's Fit.Contain rect (not the full host),
 * then apply the same artboard-space x/y/scale offsets as the graphic gizmo.
 */
function drawVideoCover(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  canvasWidth: number,
  canvasHeight: number,
  artboardWidth: number,
  artboardHeight: number,
  frame?: { xOffset: number; yOffset: number; scaleFac: number }
): void {
  const layout = getContainLayout(
    canvasWidth,
    canvasHeight,
    artboardWidth,
    artboardHeight
  );
  const destW = artboardWidth * layout.scale;
  const destH = artboardHeight * layout.scale;
  const vw = video.videoWidth || destW;
  const vh = video.videoHeight || destH;
  if (!vw || !vh || destW <= 0 || destH <= 0) {
    ctx.fillStyle = "#000";
    ctx.fillRect(layout.offsetX, layout.offsetY, destW, destH);
    return;
  }
  const userScale = Math.max(0.01, (frame?.scaleFac ?? 100) / 100);
  const cover = Math.max(destW / vw, destH / vh) * userScale;
  const dw = vw * cover;
  const dh = vh * cover;
  const cx =
    layout.offsetX + destW / 2 + (frame?.xOffset ?? 0) * layout.scale;
  const cy =
    layout.offsetY + destH / 2 + (frame?.yOffset ?? 0) * layout.scale;
  ctx.drawImage(video, cx - dw / 2, cy - dh / 2, dw, dh);
}

function evenDimension(value: number): number {
  const rounded = Math.max(2, Math.round(value));
  return rounded % 2 === 0 ? rounded : rounded + 1;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, Math.max(0, ms));
  });
}

/** Draw the Rive artboard region (Fit.Contain) into an artboard-sized export canvas. */
function drawRiveArtboard(
  ctx: CanvasRenderingContext2D,
  riveCanvas: HTMLCanvasElement,
  artboardWidth: number,
  artboardHeight: number,
  exportWidth: number,
  exportHeight: number
): void {
  const layout = getContainLayout(
    riveCanvas.width,
    riveCanvas.height,
    artboardWidth,
    artboardHeight
  );
  const srcW = artboardWidth * layout.scale;
  const srcH = artboardHeight * layout.scale;
  if (srcW <= 0 || srcH <= 0) {
    return;
  }
  ctx.drawImage(
    riveCanvas,
    layout.offsetX,
    layout.offsetY,
    srcW,
    srcH,
    0,
    0,
    exportWidth,
    exportHeight
  );
}


function waitForVideoEvent(
  video: HTMLVideoElement,
  eventName: "ended" | "loadeddata" | "seeked"
): Promise<void> {
  return new Promise((resolve, reject) => {
    const onOk = () => {
      cleanup();
      resolve();
    };
    const onErr = () => {
      cleanup();
      reject(new Error("Video playback failed."));
    };
    const cleanup = () => {
      video.removeEventListener(eventName, onOk);
      video.removeEventListener("error", onErr);
    };
    video.addEventListener(eventName, onOk, { once: true });
    video.addEventListener("error", onErr, { once: true });
  });
}

async function seekVideoTo(video: HTMLVideoElement, time: number): Promise<void> {
  if (video.readyState < 1) {
    await waitForVideoEvent(video, "loadeddata");
  }
  const max = Number.isFinite(video.duration) ? video.duration : time;
  const target = Math.max(0, Math.min(time, max));
  if (Math.abs(video.currentTime - target) < 0.01) {
    video.currentTime = target;
    return;
  }
  const seeked = waitForVideoEvent(video, "seeked");
  video.currentTime = target;
  await seeked;
}

function formatTimecode(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "0:00";
  }
  const whole = Math.floor(seconds);
  const mins = Math.floor(whole / 60);
  const secs = whole % 60;
  const tenths = Math.floor((seconds - whole) * 10);
  return `${mins}:${String(secs).padStart(2, "0")}.${tenths}`;
}

function waitFrames(count: number): Promise<void> {
  return new Promise((resolve) => {
    let remaining = count;
    const tick = () => {
      remaining -= 1;
      if (remaining <= 0) {
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

function clampInt(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function buildCalendarCells(year: number, month: number): Array<number | null> {
  const firstWeekday = new Date(year, month - 1, 1).getDay();
  const totalDays = daysInMonth(year, month);
  const cells: Array<number | null> = [];
  for (let i = 0; i < firstWeekday; i += 1) {
    cells.push(null);
  }
  for (let day = 1; day <= totalDays; day += 1) {
    cells.push(day);
  }
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  return cells;
}

type LayoutMap = {
  scale: number;
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
};

function getContainLayout(
  hostWidth: number,
  hostHeight: number,
  artboardWidth: number,
  artboardHeight: number
): LayoutMap {
  const scale = Math.min(hostWidth / artboardWidth, hostHeight / artboardHeight);
  return {
    scale,
    offsetX: (hostWidth - artboardWidth * scale) / 2,
    offsetY: (hostHeight - artboardHeight * scale) / 2,
    width: hostWidth,
    height: hostHeight,
  };
}

function applyNowDefaults(viewModelInstance: ViewModelInstance): void {
  const now = new Date();
  const hour24 = now.getHours();
  const isAm = hour24 < 12;
  let hour12 = hour24 % 12;
  if (hour12 === 0) {
    hour12 = 12;
  }

  const year = viewModelInstance.number("year_db_num");
  const month = viewModelInstance.number("month_db_num");
  const day = viewModelInstance.number("date_db_num");
  const hour = viewModelInstance.number("hour_db_num");
  const minute = viewModelInstance.number("minute_db_num");
  const am = viewModelInstance.boolean("am_db_bool");
  const atLeast = viewModelInstance.boolean("at_least_db_bool");
  const weekday = viewModelInstance.enum(WEEKDAY_PATH);

  if (year) year.value = now.getFullYear();
  if (month) month.value = now.getMonth() + 1;
  if (day) day.value = now.getDate();
  if (hour) hour.value = hour12;
  if (minute) minute.value = now.getMinutes();
  if (am) am.value = isAm;
  if (atLeast) atLeast.value = false;
  if (weekday) weekday.value = WEEKDAY_NAMES[now.getDay()];
}

function syncViewModelExceptSpanish(
  source: ViewModelInstance,
  target: ViewModelInstance
): void {
  for (const path of SYNC_NUMBER_PATHS) {
    const from = source.number(path);
    const to = target.number(path);
    if (from && to && to.value !== from.value) {
      to.value = from.value;
    }
  }
  for (const path of SYNC_STRING_PATHS) {
    const from = source.string(path);
    const to = target.string(path);
    if (from && to && to.value !== from.value) {
      to.value = from.value;
    }
  }
  for (const path of SYNC_BOOL_PATHS) {
    const from = source.boolean(path);
    const to = target.boolean(path);
    if (from && to && to.value !== from.value) {
      to.value = from.value;
    }
  }
  const fromWeekday = source.enum(WEEKDAY_PATH);
  const toWeekday = target.enum(WEEKDAY_PATH);
  if (fromWeekday && toWeekday && toWeekday.value !== fromWeekday.value) {
    toWeekday.value = fromWeekday.value;
  }
  const spanish = target.boolean("spanish_db_bool");
  if (spanish && spanish.value !== true) {
    spanish.value = true;
  }
}

const BoundStringField = ({
  path,
  label,
  viewModelInstance,
}: {
  path: string;
  label: string;
  viewModelInstance: ViewModelInstance | null;
}) => {
  const { value, setValue } = useViewModelInstanceString(path, viewModelInstance);
  return (
    <label className="graphics-gen__field">
      <span>{label}</span>
      <input
        type="text"
        className="graphics-gen__field-input--caps"
        autoCapitalize="characters"
        value={value ?? ""}
        onChange={(event) => setValue(event.target.value.toUpperCase())}
      />
    </label>
  );
};

const BoundDetainedField = ({
  viewModelInstance,
}: {
  viewModelInstance: ViewModelInstance | null;
}) => {
  const { value, setValue } = useViewModelInstanceNumber(
    "num_detained_db_num",
    viewModelInstance
  );
  const { value: atLeast, setValue: setAtLeast } = useViewModelInstanceBoolean(
    "at_least_db_bool",
    viewModelInstance
  );
  const [draft, setDraft] = useState(value === null ? "" : String(Math.round(value)));

  useEffect(() => {
    if (value !== null) {
      setDraft(String(Math.round(value)));
    }
  }, [value]);

  const commit = (raw: string) => {
    const digits = raw.replace(/\D/g, "");
    if (digits === "") {
      setDraft(value === null ? "" : String(Math.round(value)));
      return;
    }
    const next = clampInt(Number(digits), 0, 200);
    setValue(next);
    setDraft(String(next));
  };

  return (
    <div className="graphics-gen__field">
      <span>Number detained</span>
      <div className="graphics-gen__detained-row">
        <select
          className="graphics-gen__quantity-mode"
          value={atLeast ? "at_least" : "exactly"}
          onChange={(event) => setAtLeast(event.target.value === "at_least")}
          disabled={atLeast === null}
        >
          <option value="exactly">exactly</option>
          <option value="at_least">at least</option>
        </select>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          enterKeyHint="done"
          className="graphics-gen__number-input"
          value={draft}
          disabled={value === null}
          onChange={(event) => {
            const next = event.target.value.replace(/\D/g, "").slice(0, 3);
            setDraft(next);
            if (next !== "") {
              const parsed = Number(next);
              if (Number.isFinite(parsed)) {
                setValue(clampInt(parsed, 0, 200));
              }
            }
          }}
          onBlur={() => commit(draft)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commit(draft);
              (event.target as HTMLInputElement).blur();
            }
          }}
          aria-label="Number detained"
        />
      </div>
    </div>
  );
};

const BoundDateField = ({
  viewModelInstance,
}: {
  viewModelInstance: ViewModelInstance | null;
}) => {
  const { value: month, setValue: setMonth } = useViewModelInstanceNumber(
    "month_db_num",
    viewModelInstance
  );
  const { value: day, setValue: setDay } = useViewModelInstanceNumber(
    "date_db_num",
    viewModelInstance
  );
  const { value: year, setValue: setYear } = useViewModelInstanceNumber(
    "year_db_num",
    viewModelInstance
  );
  const { setValue: setWeekday } = useViewModelInstanceEnum(WEEKDAY_PATH, viewModelInstance);

  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const selectedYear = year ?? new Date().getFullYear();
  const selectedMonth = month ?? new Date().getMonth() + 1;
  const selectedDay = day ?? new Date().getDate();

  const [viewYear, setViewYear] = useState(selectedYear);
  const [viewMonth, setViewMonth] = useState(selectedMonth);

  useEffect(() => {
    if (!open) {
      return;
    }
    setViewYear(selectedYear);
    setViewMonth(selectedMonth);
  }, [open, selectedYear, selectedMonth]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const applyDate = (nextYear: number, nextMonth: number, nextDay: number) => {
    const safeDay = clampInt(nextDay, 1, daysInMonth(nextYear, nextMonth));
    setYear(nextYear);
    setMonth(nextMonth);
    setDay(safeDay);
    const weekday = WEEKDAY_NAMES[new Date(nextYear, nextMonth - 1, safeDay).getDay()];
    setWeekday(weekday);
    setOpen(false);
  };

  const shiftMonth = (delta: number) => {
    const date = new Date(viewYear, viewMonth - 1 + delta, 1);
    setViewYear(date.getFullYear());
    setViewMonth(date.getMonth() + 1);
  };

  const label =
    month !== null && day !== null && year !== null
      ? `${MONTH_LABELS[month - 1]} ${day}, ${year}`
      : "Select date";

  const cells = buildCalendarCells(viewYear, viewMonth);

  return (
    <div className="graphics-gen__field" ref={rootRef}>
      <span>Date</span>
      <button
        type="button"
        className="graphics-gen__picker-trigger"
        onClick={() => setOpen((current) => !current)}
        disabled={month === null || day === null || year === null}
      >
        <span>{label}</span>
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <rect x="3" y="5" width="18" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="1.75" />
          <path d="M3 10h18M8 3v4M16 3v4" fill="none" stroke="currentColor" strokeWidth="1.75" />
        </svg>
      </button>
      {open ? (
        <div className="graphics-gen__calendar" role="dialog" aria-label="Choose date">
          <div className="graphics-gen__calendar-header">
            <button type="button" onClick={() => shiftMonth(-1)} aria-label="Previous month">
              ‹
            </button>
            <div className="graphics-gen__calendar-title">
              {MONTH_LABELS[viewMonth - 1]} {viewYear}
            </div>
            <button type="button" onClick={() => shiftMonth(1)} aria-label="Next month">
              ›
            </button>
          </div>
          <div className="graphics-gen__calendar-weekdays">
            {["S", "M", "T", "W", "T", "F", "S"].map((weekday, index) => (
              <span key={`${weekday}-${index}`}>{weekday}</span>
            ))}
          </div>
          <div className="graphics-gen__calendar-grid">
            {cells.map((cell, index) => {
              if (cell === null) {
                return <span key={`empty-${index}`} className="graphics-gen__calendar-empty" />;
              }
              const isSelected =
                cell === selectedDay &&
                viewMonth === selectedMonth &&
                viewYear === selectedYear;
              return (
                <button
                  key={`${viewYear}-${viewMonth}-${cell}`}
                  type="button"
                  className={`graphics-gen__calendar-day${isSelected ? " is-selected" : ""}`}
                  onClick={() => applyDate(viewYear, viewMonth, cell)}
                >
                  {cell}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="graphics-gen__calendar-today"
            onClick={() => {
              const now = new Date();
              applyDate(now.getFullYear(), now.getMonth() + 1, now.getDate());
            }}
          >
            Today
          </button>
        </div>
      ) : null}
    </div>
  );
};

const BoundTimeField = ({
  viewModelInstance,
}: {
  viewModelInstance: ViewModelInstance | null;
}) => {
  const { value: hour, setValue: setHour } = useViewModelInstanceNumber(
    "hour_db_num",
    viewModelInstance
  );
  const { value: minute, setValue: setMinute } = useViewModelInstanceNumber(
    "minute_db_num",
    viewModelInstance
  );
  const { value: isAm, setValue: setIsAm } = useViewModelInstanceBoolean(
    "am_db_bool",
    viewModelInstance
  );

  const [draftHour, setDraftHour] = useState("");
  const [draftMinute, setDraftMinute] = useState("");
  const minuteInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (hour !== null) {
      setDraftHour(pad2(clampInt(Math.round(hour), 1, 12)));
    }
  }, [hour]);

  useEffect(() => {
    if (minute !== null) {
      setDraftMinute(pad2(clampInt(Math.round(minute), 0, 59)));
    }
  }, [minute]);

  const commitHour = (raw: string) => {
    const digits = raw.replace(/\D/g, "");
    if (!digits) {
      setDraftHour(pad2(hour ?? 12));
      return;
    }
    const next = clampInt(Number(digits), 1, 12);
    setHour(next);
    setDraftHour(pad2(next));
  };

  const commitMinute = (raw: string) => {
    const digits = raw.replace(/\D/g, "");
    if (digits === "") {
      setDraftMinute(pad2(minute ?? 0));
      return;
    }
    const next = clampInt(Number(digits), 0, 59);
    setMinute(next);
    setDraftMinute(pad2(next));
  };

  const disabled = hour === null || minute === null || isAm === null;

  return (
    <div className="graphics-gen__field">
      <span>Time</span>
      <div className={`graphics-gen__time-row${disabled ? " is-disabled" : ""}`}>
        {/* Always-visible inputs (not nested in <button>) so iOS "Done" commits reliably. */}
        <div className="graphics-gen__time" role="group" aria-label="Time">
          <input
            type="text"
            className="graphics-gen__time-input"
            value={draftHour}
            inputMode="numeric"
            pattern="[0-9]*"
            enterKeyHint="next"
            maxLength={2}
            disabled={disabled}
            aria-label="Hour"
            onChange={(event) => {
              const next = event.target.value.replace(/\D/g, "").slice(0, 2);
              setDraftHour(next);
              if (next !== "") {
                const parsed = Number(next);
                if (parsed >= 1 && parsed <= 12) {
                  setHour(parsed);
                }
              }
              if (next.length === 2) {
                commitHour(next);
                minuteInputRef.current?.focus();
                minuteInputRef.current?.select();
              }
            }}
            onBlur={() => commitHour(draftHour)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === ":" || event.key === "ArrowRight") {
                event.preventDefault();
                commitHour(draftHour);
                minuteInputRef.current?.focus();
                minuteInputRef.current?.select();
              }
            }}
          />
          <span className="graphics-gen__time-colon" aria-hidden="true">
            :
          </span>
          <input
            ref={minuteInputRef}
            type="text"
            className="graphics-gen__time-input"
            value={draftMinute}
            inputMode="numeric"
            pattern="[0-9]*"
            enterKeyHint="done"
            maxLength={2}
            disabled={disabled}
            aria-label="Minute"
            onChange={(event) => {
              const next = event.target.value.replace(/\D/g, "").slice(0, 2);
              setDraftMinute(next);
              if (next !== "") {
                const parsed = Number(next);
                if (parsed >= 0 && parsed <= 59) {
                  setMinute(parsed);
                }
              }
              if (next.length === 2) {
                commitMinute(next);
              }
            }}
            onBlur={() => commitMinute(draftMinute)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commitMinute(draftMinute);
                (event.target as HTMLInputElement).blur();
              }
            }}
          />
        </div>
        <select
          className="graphics-gen__time-ampm"
          value={isAm ? "AM" : "PM"}
          onChange={(event) => setIsAm(event.target.value === "AM")}
          disabled={disabled}
          aria-label="AM or PM"
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
};

type DragMode = "pan" | "scale";

type DragState = {
  mode: DragMode;
  pointerId: number;
  lastX: number;
  lastY: number;
  startDist: number;
  startScale: number;
};

const FILMSTRIP_FRAME_COUNT = 10;
const FILMSTRIP_HEIGHT_PX = 56;
const TRIM_HANDLE_MIN_GAP = 0.1;

function waitDoubleRaf(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

/** Wait until the video has a decoded frame at the current time. */
async function waitForDecodedFrame(video: HTMLVideoElement): Promise<void> {
  const anyVideo = video as HTMLVideoElement & {
    requestVideoFrameCallback?: (
      cb: (now: number, metadata: { mediaTime: number }) => void
    ) => number;
    cancelVideoFrameCallback?: (id: number) => void;
  };
  if (typeof anyVideo.requestVideoFrameCallback === "function") {
    await new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) {
          return;
        }
        settled = true;
        resolve();
      };
      const id = anyVideo.requestVideoFrameCallback!(() => finish());
      window.setTimeout(() => {
        anyVideo.cancelVideoFrameCallback?.(id);
        finish();
      }, 250);
    });
    await waitDoubleRaf();
    return;
  }
  await waitDoubleRaf();
  await waitDoubleRaf();
}

/**
 * Capture stills from the raw uploaded clip only (separate offscreen <video>,
 * never the Rive canvas / export composition).
 */
async function captureFilmstripFrames(
  videoUrl: string,
  duration: number,
  frameCount: number
): Promise<string[]> {
  if (!videoUrl || !Number.isFinite(duration) || duration <= 0) {
    return [];
  }

  const video = document.createElement("video");
  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.preload = "auto";
  // Blob URLs must NOT set crossOrigin — it can block canvas draws (black frames).
  // Keep a real layout size so the decoder paints frames (0×0 / tiny often stays black).
  video.setAttribute("playsinline", "true");
  video.setAttribute("webkit-playsinline", "true");
  video.style.cssText =
    "position:fixed;left:-10000px;top:0;width:320px;height:180px;opacity:0.01;pointer-events:none;visibility:visible;";
  document.body.appendChild(video);
  video.src = videoUrl;

  try {
    await new Promise<void>((resolve, reject) => {
      const onReady = () => {
        cleanup();
        resolve();
      };
      const onErr = () => {
        cleanup();
        reject(new Error("Could not load video for filmstrip."));
      };
      const cleanup = () => {
        video.removeEventListener("loadeddata", onReady);
        video.removeEventListener("error", onErr);
      };
      if (video.readyState >= 2 && video.videoWidth > 0) {
        resolve();
        return;
      }
      video.addEventListener("loadeddata", onReady, { once: true });
      video.addEventListener("error", onErr, { once: true });
    });

    // Kick the decoder once so later seeks paint reliably.
    try {
      await video.play();
      await waitForDecodedFrame(video);
      video.pause();
    } catch {
      /* autoplay restrictions — seeks may still work */
    }

    if (video.videoWidth <= 0 || video.videoHeight <= 0) {
      return [];
    }

    const srcW = video.videoWidth;
    const srcH = video.videoHeight;
    const thumbH = FILMSTRIP_HEIGHT_PX;
    const thumbW = Math.max(32, Math.round((srcW / srcH) * thumbH));
    const canvas = document.createElement("canvas");
    canvas.width = thumbW;
    canvas.height = thumbH;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) {
      return [];
    }

    const count = Math.max(2, frameCount);
    const frames: string[] = [];
    for (let index = 0; index < count; index += 1) {
      const t = count === 1 ? 0 : (duration * index) / (count - 1);
      // Nudge off exact 0/end — some decoders return an empty first frame at t=0.
      const seekTime = Math.min(
        Math.max(duration * 0.001, t),
        Math.max(0, duration - 0.04)
      );
      await seekVideoTo(video, seekTime);
      await waitForDecodedFrame(video);
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, thumbW, thumbH);
      ctx.drawImage(video, 0, 0, thumbW, thumbH);
      frames.push(canvas.toDataURL("image/jpeg", 0.85));
    }
    return frames;
  } finally {
    video.pause();
    video.removeAttribute("src");
    video.load();
    video.remove();
  }
}

const VideoFilmstripTrimmer = ({
  videoUrl,
  duration,
  trimStart,
  trimEnd,
  playbackTime,
  disabled,
  onChangeStart,
  onChangeEnd,
  onScrub,
}: {
  videoUrl: string;
  duration: number;
  trimStart: number;
  trimEnd: number;
  /** When set (e.g. during preview), drives the red playhead. */
  playbackTime?: number | null;
  disabled?: boolean;
  onChangeStart: (value: number) => void;
  onChangeEnd: (value: number) => void;
  onScrub: (time: number) => void;
}) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [frames, setFrames] = useState<string[]>([]);
  const [loadingFrames, setLoadingFrames] = useState(false);
  const [playhead, setPlayhead] = useState(trimStart);
  const dragRef = useRef<{
    kind: "start" | "end" | "playhead";
    pointerId: number;
    originClientX: number;
    originTime: number;
  } | null>(null);
  const trimStartRef = useRef(trimStart);
  const trimEndRef = useRef(trimEnd);
  const playheadRef = useRef(playhead);

  useEffect(() => {
    trimStartRef.current = trimStart;
    trimEndRef.current = trimEnd;
    setPlayhead((current) =>
      Math.min(trimEnd, Math.max(trimStart, current))
    );
  }, [trimStart, trimEnd]);

  useEffect(() => {
    playheadRef.current = playhead;
  }, [playhead]);

  useEffect(() => {
    if (playbackTime == null || !Number.isFinite(playbackTime)) {
      return;
    }
    if (dragRef.current) {
      return;
    }
    setPlayhead(
      Math.min(trimEndRef.current, Math.max(trimStartRef.current, playbackTime))
    );
  }, [playbackTime]);

  useEffect(() => {
    let cancelled = false;
    setFrames([]);
    if (!videoUrl || duration <= 0) {
      return;
    }
    setLoadingFrames(true);
    (async () => {
      try {
        const next = await captureFilmstripFrames(
          videoUrl,
          duration,
          FILMSTRIP_FRAME_COUNT
        );
        if (!cancelled) {
          setFrames(next);
        }
      } catch {
        if (!cancelled) {
          setFrames([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingFrames(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [duration, videoUrl]);

  const startPct = duration > 0 ? (trimStart / duration) * 100 : 0;
  const endPct = duration > 0 ? (trimEnd / duration) * 100 : 100;
  const playheadPct = duration > 0 ? (playhead / duration) * 100 : 0;

  const timeFromClientX = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track || duration <= 0) {
        return 0;
      }
      const rect = track.getBoundingClientRect();
      if (rect.width <= 0) {
        return 0;
      }
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      return ratio * duration;
    },
    [duration]
  );

  const applyDrag = useCallback(
    (clientX: number) => {
      const drag = dragRef.current;
      if (!drag) {
        return;
      }
      const delta =
        timeFromClientX(clientX) - timeFromClientX(drag.originClientX);
      const time = drag.originTime + delta;
      if (drag.kind === "start") {
        const next = Math.min(time, trimEndRef.current - TRIM_HANDLE_MIN_GAP);
        const clamped = Math.max(0, next);
        onChangeStart(clamped);
        setPlayhead(clamped);
        onScrub(clamped);
        return;
      }
      if (drag.kind === "end") {
        const next = Math.max(time, trimStartRef.current + TRIM_HANDLE_MIN_GAP);
        const clamped = Math.min(duration, next);
        onChangeEnd(clamped);
        setPlayhead(clamped);
        onScrub(clamped);
        return;
      }
      const clamped = Math.min(
        trimEndRef.current,
        Math.max(trimStartRef.current, time)
      );
      setPlayhead(clamped);
      onScrub(clamped);
    },
    [duration, onChangeEnd, onChangeStart, onScrub, timeFromClientX]
  );

  const onPointerDown = (
    kind: "start" | "end" | "playhead",
    event: React.PointerEvent<HTMLElement>
  ) => {
    if (disabled) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const originTime =
      kind === "start"
        ? trimStartRef.current
        : kind === "end"
          ? trimEndRef.current
          : playheadRef.current;
    dragRef.current = {
      kind,
      pointerId: event.pointerId,
      originClientX: event.clientX,
      originTime,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    if (kind === "playhead") {
      onScrub(originTime);
    } else if (kind === "start") {
      setPlayhead(originTime);
      onScrub(originTime);
    } else {
      setPlayhead(originTime);
      onScrub(originTime);
    }
  };

  const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    applyDrag(event.clientX);
  };

  const onPointerUp = (event: React.PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <div className="graphics-gen__trim">
      <div className="graphics-gen__trim-head">
        <span>Trim</span>
        <span className="graphics-gen__trim-range">
          {formatTimecode(trimStart)} – {formatTimecode(trimEnd)}
          <em> ({formatTimecode(Math.max(0, trimEnd - trimStart))})</em>
        </span>
      </div>
      <div className="graphics-gen__filmstrip-shell">
        <div
          className={`graphics-gen__filmstrip${disabled ? " is-disabled" : ""}`}
          ref={trackRef}
        >
          <div className="graphics-gen__filmstrip-frames" aria-hidden="true">
            {(frames.length
              ? frames
              : Array.from({ length: FILMSTRIP_FRAME_COUNT }, () => "")
            ).map((frame, index) => (
              <div
                key={`frame-${index}`}
                className="graphics-gen__filmstrip-frame"
              >
                {frame ? (
                  <img src={frame} alt="" draggable={false} />
                ) : (
                  <div className="graphics-gen__filmstrip-placeholder" />
                )}
              </div>
            ))}
          </div>
          {loadingFrames ? (
            <div className="graphics-gen__filmstrip-loading">Building strip…</div>
          ) : null}
          <div
            className="graphics-gen__filmstrip-shade graphics-gen__filmstrip-shade--left"
            style={{ width: `${startPct}%` }}
          />
          <div
            className="graphics-gen__filmstrip-shade graphics-gen__filmstrip-shade--right"
            style={{ width: `${Math.max(0, 100 - endPct)}%` }}
          />
          <div
            className="graphics-gen__filmstrip-window"
            style={{
              left: `${startPct}%`,
              width: `${Math.max(0, endPct - startPct)}%`,
            }}
          >
            <button
              type="button"
              className="graphics-gen__filmstrip-seek"
              aria-label="Scrub within trim"
              disabled={disabled}
              onPointerDown={(event) => {
                if (disabled) {
                  return;
                }
                event.preventDefault();
                event.stopPropagation();
                const time = Math.min(
                  trimEndRef.current,
                  Math.max(trimStartRef.current, timeFromClientX(event.clientX))
                );
                setPlayhead(time);
                onScrub(time);
                dragRef.current = {
                  kind: "playhead",
                  pointerId: event.pointerId,
                  originClientX: event.clientX,
                  originTime: time,
                };
                event.currentTarget.setPointerCapture(event.pointerId);
              }}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            />
            <button
              type="button"
              className="graphics-gen__filmstrip-handle graphics-gen__filmstrip-handle--start"
              aria-label="Trim start"
              disabled={disabled}
              onPointerDown={(event) => onPointerDown("start", event)}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            >
              <span className="graphics-gen__filmstrip-grip" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="graphics-gen__filmstrip-handle graphics-gen__filmstrip-handle--end"
              aria-label="Trim end"
              disabled={disabled}
              onPointerDown={(event) => onPointerDown("end", event)}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            >
              <span className="graphics-gen__filmstrip-grip" aria-hidden="true" />
            </button>
          </div>
          <div
            className="graphics-gen__filmstrip-playhead"
            style={{ left: `${playheadPct}%` }}
          >
            <button
              type="button"
              className="graphics-gen__filmstrip-playhead-knob"
              aria-label="Preview playhead"
              disabled={disabled}
              onPointerDown={(event) => onPointerDown("playhead", event)}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            />
            <button
              type="button"
              className="graphics-gen__filmstrip-playhead-line"
              aria-label="Drag playhead"
              disabled={disabled}
              onPointerDown={(event) => onPointerDown("playhead", event)}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const SegmentedToggle = <T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<{ id: T; label: string }>;
  ariaLabel: string;
}) => (
  <div className="graphics-gen__lang-toggle" role="group" aria-label={ariaLabel}>
    {options.map((option) => (
      <button
        key={option.id}
        type="button"
        className={`graphics-gen__lang-option${value === option.id ? " is-active" : ""}`}
        onClick={() => onChange(option.id)}
      >
        {option.label}
      </button>
    ))}
  </div>
);

type MapPose = { lat: number; lng: number; zoom: number };

const MapUnderlay = ({
  active,
  searchQuery,
  selectedPlace,
  onStatus,
  mapRef,
}: {
  active: boolean;
  searchQuery: string;
  selectedPlace: google.maps.places.Place | null;
  onStatus: (message: string | null) => void;
  mapRef: React.MutableRefObject<google.maps.Map | null>;
}) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;

  useEffect(() => {
    if (!active) {
      return;
    }
    let cancelled = false;

    (async () => {
      try {
        const g = await loadGoogleMaps();
        if (cancelled || !hostRef.current) {
          return;
        }
        if (!mapRef.current) {
          mapRef.current = new g.maps.Map(hostRef.current, {
            center: { lat: 40.4406, lng: -79.9959 },
            zoom: 16,
            mapTypeId: "roadmap",
            disableDefaultUI: true,
            zoomControl: true,
            gestureHandling: "greedy",
            clickableIcons: false,
            keyboardShortcuts: false,
          });
          geocoderRef.current = new g.maps.Geocoder();
        } else if (hostRef.current && mapRef.current.getDiv() !== hostRef.current) {
          mapRef.current = new g.maps.Map(hostRef.current, {
            center: mapRef.current.getCenter() || { lat: 40.4406, lng: -79.9959 },
            zoom: mapRef.current.getZoom() || 16,
            mapTypeId: "roadmap",
            disableDefaultUI: true,
            zoomControl: true,
            gestureHandling: "greedy",
            clickableIcons: false,
            keyboardShortcuts: false,
          });
          geocoderRef.current = new g.maps.Geocoder();
        } else {
          g.maps.event.trigger(mapRef.current, "resize");
        }
        onStatusRef.current(null);
      } catch (error) {
        onStatusRef.current(
          error instanceof Error ? error.message : "Could not load Google Maps."
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [active, mapRef]);

  useEffect(() => {
    if (!active || !mapRef.current) {
      return;
    }

    let cancelled = false;

    const focusGeometry = (options: {
      viewport?: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral | null;
      location?: google.maps.LatLng | google.maps.LatLngLiteral | null;
    }) => {
      if (!mapRef.current) {
        return;
      }
      if (options.viewport) {
        mapRef.current.fitBounds(options.viewport);
      } else if (options.location) {
        mapRef.current.setCenter(options.location);
        mapRef.current.setZoom(17);
      }
    };

    (async () => {
      if (selectedPlace) {
        try {
          await selectedPlace.fetchFields({
            fields: ["location", "viewport", "displayName", "formattedAddress"],
          });
          if (cancelled) {
            return;
          }
          onStatusRef.current(null);
          focusGeometry({
            viewport: selectedPlace.viewport,
            location: selectedPlace.location,
          });
        } catch {
          if (!cancelled) {
            onStatusRef.current("Could not open that place.");
          }
        }
        return;
      }

      if (!searchQuery.trim() || !geocoderRef.current) {
        return;
      }

      geocoderRef.current.geocode({ address: searchQuery.trim() }, (results, status) => {
        if (cancelled) {
          return;
        }
        if (status !== "OK" || !results?.[0]?.geometry) {
          onStatusRef.current("Could not find that location.");
          return;
        }
        onStatusRef.current(null);
        focusGeometry({
          viewport: results[0].geometry.viewport,
          location: results[0].geometry.location,
        });
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [active, mapRef, searchQuery, selectedPlace]);

  useEffect(() => {
    if (!active || !mapRef.current || !hostRef.current) {
      return;
    }
    const map = mapRef.current;
    const observer = new ResizeObserver(() => {
      window.google?.maps.event.trigger(map, "resize");
    });
    observer.observe(hostRef.current);
    return () => observer.disconnect();
  }, [active, mapRef]);

  if (!active) {
    return null;
  }

  return <div className="graphics-gen__map-underlay" ref={hostRef} />;
};

function readMapPose(map: google.maps.Map | null): MapPose | null {
  const center = map?.getCenter();
  const zoom = map?.getZoom();
  if (!center || zoom === undefined) {
    return null;
  }
  return { lat: center.lat(), lng: center.lng(), zoom };
}

const ImageTransformGizmo = ({
  hostRef,
  rive,
  viewModelInstance,
  active,
  setActive,
  enabled,
}: {
  hostRef: React.RefObject<HTMLDivElement | null>;
  rive: Rive | null;
  viewModelInstance: ViewModelInstance | null;
  active: boolean;
  setActive: (active: boolean) => void;
  enabled: boolean;
}) => {
  const { value: xOffset, setValue: setXOffset } = useViewModelInstanceNumber(
    "x_offset_db_num",
    viewModelInstance
  );
  const { value: yOffset, setValue: setYOffset } = useViewModelInstanceNumber(
    "y_offset_db_num",
    viewModelInstance
  );
  const { value: scaleFac, setValue: setScaleFac } = useViewModelInstanceNumber(
    "scalefac_db_num",
    viewModelInstance
  );

  const [layout, setLayout] = useState<LayoutMap | null>(null);
  const [artboardSize, setArtboardSize] = useState({ width: 540, height: 675 });
  const dragRef = useRef<DragState | null>(null);
  const [cursor, setCursor] = useState<"default" | "move" | "nwse-resize">("default");

  const refreshLayout = useCallback(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }
    const rect = host.getBoundingClientRect();
    const width = rive?.artboardWidth || 540;
    const height = rive?.artboardHeight || 675;
    setArtboardSize({ width, height });
    setLayout(getContainLayout(rect.width, rect.height, width, height));
  }, [hostRef, rive]);

  useEffect(() => {
    refreshLayout();
    const host = hostRef.current;
    if (!host) {
      return;
    }
    const observer = new ResizeObserver(() => refreshLayout());
    observer.observe(host);
    window.addEventListener("resize", refreshLayout);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", refreshLayout);
    };
  }, [hostRef, refreshLayout, rive]);

  useEffect(() => {
    if (!active || !enabled) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      const host = hostRef.current;
      if (!host) {
        return;
      }
      if (!host.contains(event.target as Node)) {
        setActive(false);
      }
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [active, enabled, hostRef, setActive]);

  useEffect(() => {
    if (!enabled && active) {
      setActive(false);
    }
  }, [active, enabled, setActive]);

  const safeX = xOffset ?? 0;
  const safeY = yOffset ?? 0;
  const safeScale = scaleFac ?? 100;

  const center = useMemo(() => {
    if (!layout) {
      return { x: 0, y: 0 };
    }
    return {
      x: layout.offsetX + (artboardSize.width / 2 + safeX) * layout.scale,
      y: layout.offsetY + (artboardSize.height / 2 + safeY) * layout.scale,
    };
  }, [artboardSize.height, artboardSize.width, layout, safeX, safeY]);

  const ringRadius = RING_BASE_PX * (safeScale / 100);

  const hitTest = (localX: number, localY: number): DragMode => {
    const dx = localX - center.x;
    const dy = localY - center.y;
    const dist = Math.hypot(dx, dy);
    if (Math.abs(dist - ringRadius) <= 12) {
      return "scale";
    }
    return "pan";
  };

  if (!enabled) {
    return null;
  }

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setActive(true);
    refreshLayout();
    const rect = event.currentTarget.getBoundingClientRect();
    const localX = event.clientX - rect.left;
    const localY = event.clientY - rect.top;
    const mode = hitTest(localX, localY);
    const dist = Math.max(8, Math.hypot(localX - center.x, localY - center.y));
    dragRef.current = {
      mode,
      pointerId: event.pointerId,
      lastX: event.clientX,
      lastY: event.clientY,
      startDist: dist,
      startScale: safeScale,
    };
    setCursor(mode === "scale" ? "nwse-resize" : "move");
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !layout) {
      if (active) {
        const rect = event.currentTarget.getBoundingClientRect();
        const mode = hitTest(event.clientX - rect.left, event.clientY - rect.top);
        setCursor(mode === "scale" ? "nwse-resize" : "move");
      }
      return;
    }

    if (drag.mode === "pan") {
      const dx = (event.clientX - drag.lastX) / layout.scale;
      const dy = (event.clientY - drag.lastY) / layout.scale;
      setXOffset(safeX + dx);
      setYOffset(safeY + dy);
      drag.lastX = event.clientX;
      drag.lastY = event.clientY;
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const localX = event.clientX - rect.left;
    const localY = event.clientY - rect.top;
    const dist = Math.max(8, Math.hypot(localX - center.x, localY - center.y));
    const nextScale = clamp(
      Math.round(drag.startScale * (dist / drag.startDist)),
      SCALE_MIN,
      SCALE_MAX
    );
    setScaleFac(nextScale);
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null;
      setCursor("move");
    }
  };

  return (
    <div
      className={`graphics-gen__gizmo-layer${active ? " is-active" : ""}`}
      style={{ cursor: active ? cursor : "default" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      {active && layout ? (
        <svg className="graphics-gen__gizmo" width="100%" height="100%" aria-hidden="true">
          <line
            x1={center.x - 10}
            y1={center.y}
            x2={center.x + 10}
            y2={center.y}
            className="graphics-gen__gizmo-cross"
          />
          <line
            x1={center.x}
            y1={center.y - 10}
            x2={center.x}
            y2={center.y + 10}
            className="graphics-gen__gizmo-cross"
          />
          <circle
            cx={center.x}
            cy={center.y}
            r={ringRadius}
            className="graphics-gen__gizmo-ring"
          />
          <circle cx={center.x} cy={center.y} r={5} className="graphics-gen__gizmo-center" />
        </svg>
      ) : null}
    </div>
  );
};

const PaneVideoUnderlay = ({
  hostRef,
  rive,
  viewModelInstance,
  videoUrl,
  videoPhase,
  mainVideoRef,
  bumperVideoRef,
  muted,
  onMainLoadedMetadata,
  onMainTimeUpdate,
  onMainEnded,
  onBumperEnded,
}: {
  hostRef: React.RefObject<HTMLDivElement | null>;
  rive: Rive | null;
  viewModelInstance: ViewModelInstance | null;
  videoUrl: string;
  videoPhase: VideoPhase;
  mainVideoRef?: React.RefCallback<HTMLVideoElement | null> | React.RefObject<HTMLVideoElement | null>;
  bumperVideoRef?: React.RefCallback<HTMLVideoElement | null> | React.RefObject<HTMLVideoElement | null>;
  muted?: boolean;
  onMainLoadedMetadata?: (video: HTMLVideoElement) => void;
  onMainTimeUpdate?: (video: HTMLVideoElement) => void;
  onMainEnded?: () => void;
  onBumperEnded?: () => void;
}) => {
  const { value: xOffset } = useViewModelInstanceNumber(
    "x_offset_db_num",
    viewModelInstance
  );
  const { value: yOffset } = useViewModelInstanceNumber(
    "y_offset_db_num",
    viewModelInstance
  );
  const { value: scaleFac } = useViewModelInstanceNumber(
    "scalefac_db_num",
    viewModelInstance
  );
  const [layout, setLayout] = useState<LayoutMap | null>(null);
  const [artboardSize, setArtboardSize] = useState({ width: 540, height: 675 });

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }
    const refresh = () => {
      const rect = host.getBoundingClientRect();
      const width = rive?.artboardWidth || 540;
      const height = rive?.artboardHeight || 675;
      setArtboardSize({ width, height });
      // Same Fit.Contain mapping Rive uses for the artboard inside the host.
      setLayout(getContainLayout(rect.width, rect.height, width, height));
    };
    refresh();
    const observer = new ResizeObserver(refresh);
    observer.observe(host);
    window.addEventListener("resize", refresh);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", refresh);
    };
  }, [hostRef, rive]);

  // Pin the <video> box to the artboard letterbox rect, then apply gizmo
  // offsets in artboard space (× layout.scale) so resize tracks the .riv frame.
  const artboardBoxStyle = useMemo(() => {
    if (!layout) {
      return { visibility: "hidden" } as React.CSSProperties;
    }
    return {
      left: layout.offsetX,
      top: layout.offsetY,
      width: artboardSize.width * layout.scale,
      height: artboardSize.height * layout.scale,
    } as React.CSSProperties;
  }, [artboardSize.height, artboardSize.width, layout]);

  const mainFrameStyle = useMemo(() => {
    if (!layout) {
      return artboardBoxStyle;
    }
    const tx = (xOffset ?? 0) * layout.scale;
    const ty = (yOffset ?? 0) * layout.scale;
    const s = Math.max(0.01, (scaleFac ?? 100) / 100);
    return {
      ...artboardBoxStyle,
      transform: `translate(${tx}px, ${ty}px) scale(${s})`,
    } as React.CSSProperties;
  }, [artboardBoxStyle, layout, scaleFac, xOffset, yOffset]);

  const showBumper = videoPhase === "bumper";

  return (
    <>
      <video
        ref={mainVideoRef}
        className={`graphics-gen__video-underlay${showBumper ? " is-hidden" : ""}`}
        src={videoUrl}
        playsInline
        muted={muted}
        preload="auto"
        style={mainFrameStyle}
        onLoadedMetadata={(event) => onMainLoadedMetadata?.(event.currentTarget)}
        onTimeUpdate={(event) => onMainTimeUpdate?.(event.currentTarget)}
        onEnded={() => onMainEnded?.()}
      />
      <video
        ref={bumperVideoRef}
        className={`graphics-gen__video-underlay${showBumper ? "" : " is-hidden"}`}
        src={END_BUMPER_SRC}
        playsInline
        muted={muted}
        preload="auto"
        style={artboardBoxStyle}
        onEnded={() => onBumperEnded?.()}
      />
    </>
  );
};

const RivePane = ({
  spanish,
  gizmoEnabled,
  gizmoActive,
  setGizmoActive,
  onReady,
  label,
  framing = false,
  videoUnderlay = false,
  videoUrl = null,
  videoPhase = "idle",
  mainVideoRef,
  bumperVideoRef,
  videoMuted = false,
  onMainLoadedMetadata,
  onMainTimeUpdate,
  onMainEnded,
  onBumperEnded,
}: {
  spanish: boolean;
  gizmoEnabled: boolean;
  gizmoActive: boolean;
  setGizmoActive: (active: boolean) => void;
  onReady: (payload: {
    rive: Rive;
    viewModelInstance: ViewModelInstance;
    host: HTMLDivElement;
  }) => void;
  label?: string;
  framing?: boolean;
  videoUnderlay?: boolean;
  videoUrl?: string | null;
  videoPhase?: VideoPhase;
  mainVideoRef?: React.RefCallback<HTMLVideoElement | null> | React.RefObject<HTMLVideoElement | null>;
  bumperVideoRef?: React.RefCallback<HTMLVideoElement | null> | React.RefObject<HTMLVideoElement | null>;
  videoMuted?: boolean;
  onMainLoadedMetadata?: (video: HTMLVideoElement) => void;
  onMainTimeUpdate?: (video: HTMLVideoElement) => void;
  onMainEnded?: () => void;
  onBumperEnded?: () => void;
}) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const { rive, RiveComponent } = useRive({
    src: RIV_SRC,
    artboard: ARTBOARD,
    stateMachines: STATE_MACHINE,
    autoplay: true,
    autoBind: true,
    layout: new Layout({
      fit: Fit.Contain,
      alignment: Alignment.Center,
    }),
  });

  const viewModelInstance = rive?.viewModelInstance ?? null;

  useEffect(() => {
    if (!rive) {
      return;
    }
    rive.resizeDrawingSurfaceToCanvas();
    const onResize = () => rive.resizeDrawingSurfaceToCanvas();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [rive]);

  useEffect(() => {
    if (!viewModelInstance) {
      return;
    }
    const spanishProp = viewModelInstance.boolean("spanish_db_bool");
    if (spanishProp) {
      spanishProp.value = spanish;
    }
  }, [spanish, viewModelInstance]);

  useEffect(() => {
    if (!rive || !viewModelInstance || !hostRef.current) {
      return;
    }
    onReady({ rive, viewModelInstance, host: hostRef.current });
  }, [onReady, rive, viewModelInstance]);

  return (
    <div
      className={`graphics-gen__pane${framing ? " is-framing" : ""}${
        videoUnderlay ? " is-video" : ""
      }`}
    >
      {label ? <div className="graphics-gen__pane-label">{label}</div> : null}
      <div className="graphics-gen__canvas-wrap" ref={hostRef}>
        {videoUnderlay && videoUrl ? (
          <PaneVideoUnderlay
            hostRef={hostRef}
            rive={rive}
            viewModelInstance={viewModelInstance}
            videoUrl={videoUrl}
            videoPhase={videoPhase}
            mainVideoRef={mainVideoRef}
            bumperVideoRef={bumperVideoRef}
            muted={videoMuted}
            onMainLoadedMetadata={onMainLoadedMetadata}
            onMainTimeUpdate={onMainTimeUpdate}
            onMainEnded={onMainEnded}
            onBumperEnded={onBumperEnded}
          />
        ) : null}
        <RiveComponent className="graphics-gen__canvas" />
        <ImageTransformGizmo
          hostRef={hostRef}
          rive={rive}
          viewModelInstance={viewModelInstance}
          active={gizmoActive}
          setActive={setGizmoActive}
          enabled={gizmoEnabled && !framing}
        />
      </div>
    </div>
  );
};

const GraphicsGeneratorPage = ({ setPage }: { setPage: pagesetter }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const mainVideoRef = useRef<HTMLVideoElement | null>(null);
  const bumperVideoRef = useRef<HTMLVideoElement | null>(null);
  const secondaryMainVideoRef = useRef<HTMLVideoElement | null>(null);
  const secondaryBumperVideoRef = useRef<HTMLVideoElement | null>(null);
  const [imageName, setImageName] = useState<string | null>(null);
  const [videoName, setVideoName] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [videoPhase, setVideoPhase] = useState<VideoPhase>("idle");
  const [videoBusy, setVideoBusy] = useState(false);
  const [exportProgress, setExportProgress] = useState<number | null>(null);
  const [playheadTime, setPlayheadTime] = useState<number | null>(null);
  const playheadTimeRef = useRef(0);
  const prevLangModeRef = useRef<LangMode>("english");
  const trimRef = useRef({ start: 0, end: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [gizmoActive, setGizmoActive] = useState(false);
  const [langMode, setLangMode] = useState<LangMode>("english");
  const [mediaMode, setMediaMode] = useState<MediaMode>("image");
  const [mapFraming, setMapFraming] = useState(false);
  const [locationDraft, setLocationDraft] = useState("");
  const [mapSearchQuery, setMapSearchQuery] = useState("");
  const [selectedPlace, setSelectedPlace] = useState<google.maps.places.Place | null>(null);
  const [mapStatus, setMapStatus] = useState<string | null>(null);
  const [confirmingMap, setConfirmingMap] = useState(false);
  const [placeSuggestions, setPlaceSuggestions] = useState<
    Array<{ id: string; description: string; prediction: google.maps.places.PlacePrediction }>
  >([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const autocompleteSessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const suggestionRequestIdRef = useRef(0);
  const locationFieldRef = useRef<HTMLDivElement | null>(null);
  const outroFiredRef = useRef(false);
  const mainClipDoneRef = useRef(false);
  const videoGenRef = useRef(0);

  const masterRef = useRef<{
    rive: Rive;
    viewModelInstance: ViewModelInstance;
    host: HTMLDivElement;
  } | null>(null);
  const secondaryRef = useRef<{
    rive: Rive;
    viewModelInstance: ViewModelInstance;
    host: HTMLDivElement;
  } | null>(null);
  const lastImageBytesRef = useRef<Uint8Array | null>(null);
  const didInitDefaultsRef = useRef(false);
  const [masterTick, setMasterTick] = useState(0);
  const [secondaryTick, setSecondaryTick] = useState(0);

  const mapsConfigured = Boolean(getGoogleMapsApiKey());
  const framingActive = mediaMode === "map" && mapFraming;
  const videoActive = mediaMode === "video" && Boolean(videoUrl);

  useEffect(() => {
    trimRef.current = { start: trimStart, end: trimEnd };
  }, [trimStart, trimEnd]);

  useEffect(() => {
    const start = trimStart;
    const end = trimEnd > start ? trimEnd : start;
    const next =
      playheadTime == null
        ? start
        : Math.min(end, Math.max(start, playheadTime));
    playheadTimeRef.current = next;
  }, [playheadTime, trimEnd, trimStart]);

  const onMasterReady = useCallback(
    (payload: { rive: Rive; viewModelInstance: ViewModelInstance; host: HTMLDivElement }) => {
      masterRef.current = payload;
      if (!didInitDefaultsRef.current) {
        didInitDefaultsRef.current = true;
        applyNowDefaults(payload.viewModelInstance);
      }
      setMasterTick((value) => value + 1);
    },
    []
  );

  const onSecondaryReady = useCallback(
    (payload: { rive: Rive; viewModelInstance: ViewModelInstance; host: HTMLDivElement }) => {
      secondaryRef.current = payload;
      const spanish = payload.viewModelInstance.boolean("spanish_db_bool");
      if (spanish) {
        spanish.value = true;
      }
      setSecondaryTick((value) => value + 1);
    },
    []
  );

  useEffect(() => {
    const master = masterRef.current?.viewModelInstance;
    if (!master) {
      return;
    }
    const spanish = master.boolean("spanish_db_bool");
    if (!spanish) {
      return;
    }
    if (langMode === "english") {
      spanish.value = false;
    } else if (langMode === "spanish") {
      spanish.value = true;
    } else {
      spanish.value = false;
    }
  }, [langMode, masterTick]);

  // Entering Both view: fire intro on the language pane(s) that just appeared.
  useEffect(() => {
    const prev = prevLangModeRef.current;
    if (langMode !== "both") {
      prevLangModeRef.current = langMode;
      return;
    }
    if (prev === "both") {
      return;
    }
    const secondary = secondaryRef.current?.viewModelInstance;
    if (!secondary) {
      // Secondary pane still mounting — retry when secondaryTick updates.
      return;
    }
    const previewOn = !(videoBusy || exporting);
    setPreviewBool(secondary, previewOn);
    fireRiveTrigger(secondary, INTRO_TRIGGER);
    if (prev === "spanish") {
      // Master just flipped from Spanish-only to English in Both view.
      const masterVmi = masterRef.current?.viewModelInstance;
      if (masterVmi) {
        setPreviewBool(masterVmi, previewOn);
        fireRiveTrigger(masterVmi, INTRO_TRIGGER);
      }
    }
    prevLangModeRef.current = "both";
  }, [exporting, langMode, secondaryTick, videoBusy]);

  useEffect(() => {
    if (langMode !== "both") {
      secondaryRef.current = null;
      return;
    }
    const master = masterRef.current?.viewModelInstance;
    const secondary = secondaryRef.current?.viewModelInstance;
    if (!master || !secondary) {
      return;
    }

    let raf = 0;
    const loop = () => {
      syncViewModelExceptSpanish(master, secondary);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [langMode, masterTick, secondaryTick]);

  useEffect(() => {
    if (langMode !== "both") {
      return;
    }
    const secondary = secondaryRef.current?.viewModelInstance;
    const bytes = lastImageBytesRef.current;
    if (!secondary || !bytes) {
      return;
    }
    let cancelled = false;
    (async () => {
      const decoded = await decodeImage(bytes);
      if (cancelled) {
        decoded.unref();
        return;
      }
      secondary.image(IMAGE_PATH)!.value = decoded;
      decoded.unref();
    })();
    return () => {
      cancelled = true;
    };
  }, [langMode, secondaryTick]);

  const applyImageBytes = useCallback(async (bytes: Uint8Array, fileName: string) => {
    lastImageBytesRef.current = bytes;
    const decoded = await decodeImage(bytes);
    const masterImage = masterRef.current?.viewModelInstance.image(IMAGE_PATH);
    if (masterImage) {
      masterImage.value = decoded;
    }
    const secondaryImage = secondaryRef.current?.viewModelInstance.image(IMAGE_PATH);
    if (secondaryImage) {
      secondaryImage.value = decoded;
    }
    decoded.unref();
    setImageName(fileName);
  }, []);

  const clearGraphicImage = useCallback(() => {
    lastImageBytesRef.current = null;
    const masterImage = masterRef.current?.viewModelInstance.image(IMAGE_PATH);
    if (masterImage) {
      masterImage.value = null;
    }
    const secondaryImage = secondaryRef.current?.viewModelInstance.image(IMAGE_PATH);
    if (secondaryImage) {
      secondaryImage.value = null;
    }
    setImageName(null);
  }, []);

  const setPreviewMode = useCallback((enabled: boolean) => {
    setPreviewBool(masterRef.current?.viewModelInstance, enabled);
    setPreviewBool(secondaryRef.current?.viewModelInstance, enabled);
  }, []);

  const forEachMainVideo = useCallback((fn: (video: HTMLVideoElement) => void) => {
    const main = mainVideoRef.current;
    const secondary = secondaryMainVideoRef.current;
    if (main) {
      fn(main);
    }
    if (secondary) {
      fn(secondary);
    }
  }, []);

  const forEachBumperVideo = useCallback((fn: (video: HTMLVideoElement) => void) => {
    const bumper = bumperVideoRef.current;
    const secondary = secondaryBumperVideoRef.current;
    if (bumper) {
      fn(bumper);
    }
    if (secondary) {
      fn(secondary);
    }
  }, []);

  const syncSecondaryToMain = useCallback((time: number) => {
    const secondary = secondaryMainVideoRef.current;
    if (!secondary) {
      return;
    }
    if (Math.abs(secondary.currentTime - time) > 0.08) {
      try {
        secondary.currentTime = time;
      } catch {
        /* ignore */
      }
    }
  }, []);

  const stopVideoPlayback = useCallback(() => {
    videoGenRef.current += 1;
    const resumeAt = trimRef.current.start || 0;
    forEachMainVideo((video) => {
      video.pause();
      try {
        video.currentTime = resumeAt;
      } catch {
        /* ignore seek errors before metadata */
      }
    });
    forEachBumperVideo((video) => {
      video.pause();
      try {
        video.currentTime = 0;
      } catch {
        /* ignore */
      }
    });
    outroFiredRef.current = false;
    mainClipDoneRef.current = false;
    setPlayheadTime(resumeAt);
    setVideoPhase("idle");
    setVideoBusy(false);
    setPreviewMode(true);
  }, [forEachBumperVideo, forEachMainVideo, setPreviewMode]);

  const clearVideo = useCallback(() => {
    stopVideoPlayback();
    setVideoUrl((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      return null;
    });
    setVideoName(null);
    setVideoDuration(0);
    setTrimStart(0);
    setTrimEnd(0);
  }, [stopVideoPlayback]);

  const fireIntroOutro = useCallback((path: typeof INTRO_TRIGGER | typeof OUTRO_TRIGGER) => {
    fireRiveTrigger(masterRef.current?.viewModelInstance, path);
    fireRiveTrigger(secondaryRef.current?.viewModelInstance, path);
  }, []);

  useEffect(() => {
    const enabled = mediaMode === "video";
    setVideoModeBool(masterRef.current?.viewModelInstance, enabled);
    setVideoModeBool(secondaryRef.current?.viewModelInstance, enabled);
    // Editing/idle → preview on; playback sets this false explicitly.
    if (enabled && !videoBusy) {
      setPreviewBool(masterRef.current?.viewModelInstance, true);
      setPreviewBool(secondaryRef.current?.viewModelInstance, true);
    } else if (!enabled) {
      setPreviewBool(masterRef.current?.viewModelInstance, false);
      setPreviewBool(secondaryRef.current?.viewModelInstance, false);
    }
  }, [mediaMode, masterTick, secondaryTick, videoBusy]);

  useEffect(() => {
    return () => {
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [videoUrl]);

  const applyImageFile = useCallback(
    async (file: File) => {
      if (!file.type.startsWith("image/")) {
        setExportError("Please choose an image file.");
        return;
      }
      setExportError(null);
      clearVideo();
      setMediaMode("image");
      setMapFraming(false);
      const buffer = await file.arrayBuffer();
      await applyImageBytes(new Uint8Array(buffer), file.name);
    },
    [applyImageBytes, clearVideo]
  );

  const applyVideoFile = useCallback(
    (file: File) => {
      if (!file.type.startsWith("video/")) {
        setExportError("Please choose a video file.");
        return;
      }
      setExportError(null);
      clearGraphicImage();
      setMapFraming(false);
      setGizmoActive(false);
      stopVideoPlayback();
      const url = URL.createObjectURL(file);
      setVideoUrl((current) => {
        if (current) {
          URL.revokeObjectURL(current);
        }
        return url;
      });
      setVideoName(file.name);
      setVideoDuration(0);
      setTrimStart(0);
      setTrimEnd(0);
      setMediaMode("video");
    },
    [clearGraphicImage, stopVideoPlayback]
  );

  const applyMediaFile = useCallback(
    async (file: File) => {
      if (file.type.startsWith("video/")) {
        applyVideoFile(file);
        return;
      }
      if (file.type.startsWith("image/")) {
        await applyImageFile(file);
        return;
      }
      setExportError("Please choose a photo or video file.");
    },
    [applyImageFile, applyVideoFile]
  );

  const onFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await applyMediaFile(file);
    }
    event.target.value = "";
  };

  const onDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      await applyMediaFile(file);
    }
  };

  const handleMediaModeChange = (mode: "file" | "map") => {
    setExportError(null);
    setMapStatus(null);
    if (mode === "map") {
      if (!mapsConfigured) {
        setMapStatus("Add REACT_APP_GOOGLE_MAPS_API_KEY to frontend/.env.local and restart.");
        setMapFraming(false);
        return;
      }
      clearGraphicImage();
      clearVideo();
      setMediaMode("map");
      setMapFraming(true);
      setGizmoActive(false);
      return;
    }
    setMapFraming(false);
    setMediaMode(videoUrl ? "video" : "image");
  };

  const maybeFireOutro = useCallback(
    (video: HTMLVideoElement) => {
      if (outroFiredRef.current) {
        return;
      }
      const end = trimRef.current.end || video.duration;
      const start = trimRef.current.start || 0;
      if (!Number.isFinite(end) || end <= start) {
        return;
      }
      if (video.currentTime >= Math.max(start, end - OUTRO_LEAD_SECONDS)) {
        outroFiredRef.current = true;
        fireIntroOutro(OUTRO_TRIGGER);
      }
    },
    [fireIntroOutro]
  );

  const handleMainVideoEnded = useCallback(async () => {
    if (mainClipDoneRef.current) {
      return;
    }
    mainClipDoneRef.current = true;
    const gen = videoGenRef.current;
    const bumper = bumperVideoRef.current;
    if (!outroFiredRef.current) {
      outroFiredRef.current = true;
      fireIntroOutro(OUTRO_TRIGGER);
    }
    forEachMainVideo((video) => video.pause());
    if (!bumper) {
      setVideoPhase("done");
      setVideoBusy(false);
      setPreviewMode(true);
      return;
    }
    setVideoPhase("bumper");
    try {
      const bumpers = [bumper, secondaryBumperVideoRef.current].filter(
        (video): video is HTMLVideoElement => Boolean(video)
      );
      await Promise.all(bumpers.map((video) => seekVideoTo(video, 0)));
      await Promise.all(bumpers.map((video) => video.play()));
      if (gen !== videoGenRef.current) {
        return;
      }
    } catch (error) {
      if (gen === videoGenRef.current) {
        setVideoPhase("done");
        setVideoBusy(false);
        setPreviewMode(true);
        setExportError(
          error instanceof Error ? error.message : "Could not play the end bumper."
        );
      }
    }
  }, [fireIntroOutro, forEachMainVideo, setPreviewMode]);

  const onMainVideoTimeUpdate = useCallback(
    (video: HTMLVideoElement) => {
      if (mainClipDoneRef.current) {
        return;
      }
      setPlayheadTime(video.currentTime);
      syncSecondaryToMain(video.currentTime);
      maybeFireOutro(video);
      const end = trimRef.current.end;
      if (Number.isFinite(end) && end > 0 && video.currentTime >= end - 0.04) {
        forEachMainVideo((clip) => clip.pause());
        void handleMainVideoEnded();
      }
    },
    [forEachMainVideo, handleMainVideoEnded, maybeFireOutro, syncSecondaryToMain]
  );

  const playVideoSequence = useCallback(async () => {
    const main = mainVideoRef.current;
    if (!main || !videoUrl) {
      setExportError("Upload a video first.");
      return;
    }
    const start = trimRef.current.start;
    const end = trimRef.current.end;
    if (!(end > start)) {
      setExportError("Set a valid trim range before playing.");
      return;
    }
    // Resume from the red playhead (within the trim). If it's at the end, loop to start.
    let from = Math.min(
      end - 0.05,
      Math.max(start, playheadTimeRef.current)
    );
    if (!(from < end - 0.04)) {
      from = start;
    }
    const gen = ++videoGenRef.current;
    setExportError(null);
    setVideoBusy(true);
    setPreviewMode(false);
    outroFiredRef.current = false;
    mainClipDoneRef.current = false;
    forEachBumperVideo((video) => {
      video.pause();
      try {
        video.currentTime = 0;
      } catch {
        /* ignore */
      }
    });
    try {
      const mains = [main, secondaryMainVideoRef.current].filter(
        (video): video is HTMLVideoElement => Boolean(video)
      );
      for (const video of mains) {
        video.pause();
      }
      await Promise.all(mains.map((video) => seekVideoTo(video, from)));
      if (gen !== videoGenRef.current) {
        return;
      }
      setPlayheadTime(from);
      setVideoPhase("main");
      // Always fire intro on preview so copy is visible even when resuming mid-clip.
      // (Export still only fires intro at trim start.)
      fireIntroOutro(INTRO_TRIGGER);
      if (end - from <= OUTRO_LEAD_SECONDS) {
        outroFiredRef.current = true;
        fireIntroOutro(OUTRO_TRIGGER);
      }
      await Promise.all(mains.map((video) => video.play()));
    } catch (error) {
      if (gen === videoGenRef.current) {
        setVideoBusy(false);
        setVideoPhase("idle");
        setPreviewMode(true);
        setExportError(
          error instanceof Error ? error.message : "Could not play the video."
        );
      }
    }
  }, [fireIntroOutro, forEachBumperVideo, setPreviewMode, videoUrl]);

  const handleBumperEnded = useCallback(() => {
    forEachBumperVideo((video) => video.pause());
    setVideoPhase("done");
    setVideoBusy(false);
    setPreviewMode(true);
  }, [forEachBumperVideo, setPreviewMode]);

  const toggleVideoPreview = useCallback(() => {
    if (videoBusy) {
      stopVideoPlayback();
      return;
    }
    void playVideoSequence();
  }, [playVideoSequence, stopVideoPlayback, videoBusy]);

  useEffect(() => {
    if (mediaMode !== "map" || !mapsConfigured) {
      setPlaceSuggestions([]);
      return;
    }

    const query = locationDraft.trim();
    if (query.length < 2) {
      setPlaceSuggestions([]);
      return;
    }

    let cancelled = false;
    const requestId = ++suggestionRequestIdRef.current;
    const timeout = window.setTimeout(async () => {
      try {
        const g = await loadGoogleMaps();
        if (cancelled) {
          return;
        }
        const { AutocompleteSuggestion, AutocompleteSessionToken } =
          (await g.maps.importLibrary("places")) as google.maps.PlacesLibrary;

        if (!autocompleteSessionTokenRef.current) {
          autocompleteSessionTokenRef.current = new AutocompleteSessionToken();
        }

        const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: query,
          sessionToken: autocompleteSessionTokenRef.current,
        });

        if (cancelled || requestId !== suggestionRequestIdRef.current) {
          return;
        }

        const nextSuggestions = suggestions
          .map((suggestion, index) => {
            const prediction = suggestion.placePrediction;
            if (!prediction) {
              return null;
            }
            return {
              id: `${prediction.placeId || "prediction"}-${index}`,
              description: prediction.text.toString(),
              prediction,
            };
          })
          .filter(
            (
              suggestion
            ): suggestion is {
              id: string;
              description: string;
              prediction: google.maps.places.PlacePrediction;
            } => Boolean(suggestion)
          )
          .slice(0, 6);

        setPlaceSuggestions(nextSuggestions);
        setSuggestionsOpen(nextSuggestions.length > 0);
      } catch (error) {
        if (!cancelled) {
          setPlaceSuggestions([]);
          setMapStatus(
            error instanceof Error
              ? error.message
              : "Places autocomplete failed. Enable Places API (New) for this key."
          );
        }
      }
    }, 220);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [locationDraft, mapsConfigured, mediaMode]);

  useEffect(() => {
    if (!suggestionsOpen) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (!locationFieldRef.current?.contains(event.target as Node)) {
        setSuggestionsOpen(false);
      }
    };
    window.addEventListener("mousedown", onPointerDown);
    return () => window.removeEventListener("mousedown", onPointerDown);
  }, [suggestionsOpen]);

  const handleMapSearch = () => {
    const next = locationDraft.trim();
    if (!next) {
      setMapStatus("Enter a location to search.");
      return;
    }
    setSelectedPlace(null);
    setMapSearchQuery(next);
    setSuggestionsOpen(false);
  };

  const handleSuggestionSelect = async (suggestion: {
    description: string;
    prediction: google.maps.places.PlacePrediction;
  }) => {
    setLocationDraft(suggestion.description);
    setPlaceSuggestions([]);
    setSuggestionsOpen(false);
    setMapStatus(null);
    try {
      const place = suggestion.prediction.toPlace();
      setSelectedPlace(place);
      setMapSearchQuery(suggestion.description);
      autocompleteSessionTokenRef.current = null;
    } catch (error) {
      setMapStatus(
        error instanceof Error ? error.message : "Could not select that place."
      );
    }
  };

  const handleConfirmMap = async () => {
    setExportError(null);
    setMapStatus(null);
    const pose = readMapPose(mapRef.current);
    if (!pose) {
      setMapStatus("Map is still loading — try again in a moment.");
      return;
    }
    setConfirmingMap(true);
    try {
      const bytes = await fetchStaticMapBytes(pose);
      await applyImageBytes(bytes, `map-${pose.lat.toFixed(4)}_${pose.lng.toFixed(4)}.png`);
      setMapFraming(false);
    } catch (error) {
      setMapStatus(
        error instanceof Error
          ? error.message
          : "Could not capture map. Enable Maps JavaScript + Static Maps APIs for this key."
      );
    } finally {
      setConfirmingMap(false);
    }
  };

  const handleExportPng = async () => {
    const masterVmi = masterRef.current?.viewModelInstance;
    const enName = buildExportFilename(masterVmi, "ENGLISH");
    const esName = buildExportFilename(masterVmi, "SPANISH");

    if (langMode === "both") {
      const enCanvas = masterRef.current?.host.querySelector("canvas");
      const esCanvas = secondaryRef.current?.host.querySelector("canvas");
      if (!(enCanvas instanceof HTMLCanvasElement) || !(esCanvas instanceof HTMLCanvasElement)) {
        setExportError("Graphics are still loading — try again in a moment.");
        return;
      }
      const enBlob = await new Promise<Blob | null>((resolve) =>
        enCanvas.toBlob((blob) => resolve(blob), "image/png")
      );
      const esBlob = await new Promise<Blob | null>((resolve) =>
        esCanvas.toBlob((blob) => resolve(blob), "image/png")
      );
      if (!enBlob || !esBlob) {
        await saveCanvasPng(enCanvas, enName);
        await saveCanvasPng(esCanvas, esName);
        return;
      }
      await saveFiles([
        { blob: enBlob, filename: enName },
        { blob: esBlob, filename: esName },
      ]);
      return;
    }

    const master = masterRef.current;
    const canvas = master?.host.querySelector("canvas");
    const spanish = master?.viewModelInstance.boolean("spanish_db_bool");
    if (!(canvas instanceof HTMLCanvasElement) || !spanish) {
      setExportError("Graphic is still loading — try again in a moment.");
      return;
    }

    spanish.value = false;
    await waitFrames(3);
    const enBlob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((blob) => resolve(blob), "image/png")
    );

    spanish.value = true;
    await waitFrames(3);
    const esBlob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((blob) => resolve(blob), "image/png")
    );

    if (langMode === "english") {
      spanish.value = false;
    } else if (langMode === "spanish") {
      spanish.value = true;
    }

    if (enBlob && esBlob) {
      await saveFiles([
        { blob: enBlob, filename: enName },
        { blob: esBlob, filename: esName },
      ]);
      return;
    }
    if (enBlob) {
      await saveBlob(enBlob, enName);
    } else {
      await saveCanvasPng(canvas, enName);
    }
    if (esBlob) {
      await saveBlob(esBlob, esName);
    } else {
      await saveCanvasPng(canvas, esName);
    }
  };

  const handleExportVideo = async () => {
    const master = masterRef.current;
    const riveCanvas = master?.host.querySelector("canvas");
    const main = mainVideoRef.current;
    const bumper = bumperVideoRef.current;
    const masterVmi = master?.viewModelInstance;
    const spanish = masterVmi?.boolean("spanish_db_bool");
    if (
      !master ||
      !masterVmi ||
      !(riveCanvas instanceof HTMLCanvasElement) ||
      !main ||
      !videoUrl ||
      !spanish
    ) {
      setExportError("Upload a video and wait for the graphic to load.");
      return;
    }

    const start = trimRef.current.start;
    const end = trimRef.current.end;
    if (!(end > start)) {
      setExportError("Set a valid trim range before exporting.");
      return;
    }

    // Export exactly the Rive artboard frame (not the letterboxed host canvas).
    const artboardWidth = master?.rive.artboardWidth || 1080;
    const artboardHeight = master?.rive.artboardHeight || 1350;
    const exportWidth = evenDimension(artboardWidth);
    const exportHeight = evenDimension(artboardHeight);
    const out = document.createElement("canvas");
    out.width = exportWidth;
    out.height = exportHeight;
    const ctx = out.getContext("2d", { alpha: false });
    if (!ctx) {
      setExportError("Could not create a recording canvas.");
      return;
    }

    const frame = readVideoFrame(master?.viewModelInstance);
    const previousSpanish = spanish.value;
    const languages: Array<"ENGLISH" | "SPANISH"> = ["ENGLISH", "SPANISH"];
    const gen = ++videoGenRef.current;
    const fps = 30;
    const frameDuration = 1 / fps;

    setVideoBusy(true);
    setPreviewMode(false);
    setExportError(null);
    setExportProgress(0);

    // Pause the secondary main clip during encode (keeps CPU free), but we'll
    // still play the secondary bumper so Both-view mirrors the left pane.
    secondaryMainVideoRef.current?.pause();

    const clipDuration = Math.max(0.001, end - start);
    const bumperDuration =
      bumper && Number.isFinite(bumper.duration) ? Math.max(0, bumper.duration) : 0;
    const mediaDuration = clipDuration + bumperDuration;

    const reportProgress = (langIndex: number, localFraction: number) => {
      const clamped = Math.min(1, Math.max(0, localFraction));
      setExportProgress(
        Math.min(99, Math.round(((langIndex + clamped) / languages.length) * 100))
      );
    };

    const recordLanguage = async (
      language: "ENGLISH" | "SPANISH",
      langIndex: number
    ): Promise<Blob> => {
      spanish.value = language === "SPANISH";
      // Keep master English/Spanish overlay in sync; secondary pane stays paused.
      setPreviewBool(masterVmi, false);
      setPreviewBool(secondaryRef.current?.viewModelInstance, false);
      reportProgress(langIndex, 0);
      await waitFrames(6);
      if (gen !== videoGenRef.current) {
        throw new Error("Export cancelled.");
      }

      const output = new Output({
        format: new Mp4OutputFormat({ fastStart: "in-memory" }),
        target: new BufferTarget(),
      });
      const videoSource = new CanvasSource(out, {
        codec: "avc",
        bitrate: QUALITY_HIGH,
      });
      output.addVideoTrack(videoSource);

      let audioSource: MediaStreamAudioTrackSource | null = null;
      let audioTrack: MediaStreamAudioTrack | null = null;
      try {
        if (await canEncodeAudio("aac")) {
          const captureStream = (
            main as HTMLVideoElement & { captureStream?: () => MediaStream }
          ).captureStream;
          const mainStream =
            typeof captureStream === "function" ? captureStream.call(main) : null;
          audioTrack =
            (mainStream?.getAudioTracks()[0] as MediaStreamAudioTrack | undefined) ??
            null;
          if (audioTrack) {
            audioSource = new MediaStreamAudioTrackSource(audioTrack, {
              codec: "aac",
              bitrate: QUALITY_HIGH,
            });
            output.addAudioTrack(audioSource);
          }
        }
      } catch {
        audioSource = null;
        audioTrack = null;
      }

      let phase: VideoPhase = "main";
      const paint = () => {
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, exportWidth, exportHeight);
        const underlay = phase === "bumper" && bumper ? bumper : main;
        if (!underlay.paused || underlay.currentTime > 0 || phase === "main") {
          // Export canvas == artboard, so video covers the full frame.
          drawVideoCover(
            ctx,
            underlay,
            exportWidth,
            exportHeight,
            exportWidth,
            exportHeight,
            phase === "bumper" ? undefined : frame
          );
        }
        drawRiveArtboard(
          ctx,
          riveCanvas,
          artboardWidth,
          artboardHeight,
          exportWidth,
          exportHeight
        );
      };

      outroFiredRef.current = false;
      mainClipDoneRef.current = false;
      setVideoPhase("main");
      phase = "main";
      if (bumper) {
        bumper.pause();
        await seekVideoTo(bumper, 0);
      }
      secondaryBumperVideoRef.current?.pause();
      main.pause();
      await seekVideoTo(main, start);
      await waitForDecodedFrame(main);
      setPlayheadTime(start);
      if (gen !== videoGenRef.current) {
        throw new Error("Export cancelled.");
      }

      // Intro at trim start only for export (preview may fire mid-clip separately).
      fireIntroOutro(INTRO_TRIGGER);
      if (end - start <= OUTRO_LEAD_SECONDS) {
        outroFiredRef.current = true;
        fireIntroOutro(OUTRO_TRIGGER);
      }
      await waitFrames(4);
      paint();

      await output.start();
      // Don't capture silence/noise before the clip actually rolls.
      audioSource?.pause();

      let frameIndex = 0;
      const wallStart = performance.now();
      const captureFrame = async () => {
        if (gen !== videoGenRef.current) {
          throw new Error("Export cancelled.");
        }
        paint();
        // Wall-clock timestamps keep video duration aligned with live audio even
        // if the encoder briefly falls behind (avoids frozen-video / running-audio).
        const timestamp = Math.max(0, (performance.now() - wallStart) / 1000);
        await videoSource.add(timestamp, frameDuration);
        frameIndex += 1;
        const targetMs = wallStart + frameIndex * frameDuration * 1000;
        const delay = targetMs - performance.now();
        if (delay > 1) {
          await sleep(delay);
        }
      };

      // Warm up the encoder on a held first frame so the opening isn't choppy.
      for (let i = 0; i < 3; i += 1) {
        await captureFrame();
      }

      audioSource?.resume();
      await main.play();

      try {
        while (gen === videoGenRef.current) {
          maybeFireOutro(main);
          setPlayheadTime(main.currentTime);
          reportProgress(
            langIndex,
            Math.min(1, Math.max(0, (main.currentTime - start) / mediaDuration))
          );
          if (main.ended || main.currentTime >= end - 0.04) {
            break;
          }
          await captureFrame();
        }
        main.pause();
        mainClipDoneRef.current = true;
        if (gen !== videoGenRef.current) {
          throw new Error("Export cancelled.");
        }
        if (!outroFiredRef.current) {
          outroFiredRef.current = true;
          fireIntroOutro(OUTRO_TRIGGER);
        }
        if (bumper) {
          phase = "bumper";
          setVideoPhase("bumper");
          const bumpers = [bumper, secondaryBumperVideoRef.current].filter(
            (video): video is HTMLVideoElement => Boolean(video)
          );
          await Promise.all(bumpers.map((video) => seekVideoTo(video, 0)));
          await Promise.all(bumpers.map((video) => waitForDecodedFrame(video)));
          await Promise.all(bumpers.map((video) => video.play()));
          while (gen === videoGenRef.current && !bumper.ended) {
            reportProgress(
              langIndex,
              Math.min(
                1,
                (clipDuration + Math.max(0, bumper.currentTime)) / mediaDuration
              )
            );
            await captureFrame();
          }
          for (const video of bumpers) {
            video.pause();
          }
        }
        reportProgress(langIndex, 1);
      } finally {
        // Stop audio capture at the same moment video frames stop — prevents
        // "video freezes / audio keeps going" from encoder backlog.
        audioSource?.pause();
        try {
          audioTrack?.stop();
        } catch {
          /* ignore */
        }
        main.pause();
        bumper?.pause();
        secondaryBumperVideoRef.current?.pause();
      }

      await output.finalize();
      const buffer = output.target.buffer;
      if (!buffer) {
        throw new Error("MP4 export produced an empty file.");
      }
      return new Blob([buffer], { type: "video/mp4" });
    };

    try {
      const finished: Array<{ language: "ENGLISH" | "SPANISH"; blob: Blob }> = [];
      for (let langIndex = 0; langIndex < languages.length; langIndex += 1) {
        if (gen !== videoGenRef.current) {
          return;
        }
        const language = languages[langIndex];
        const blob = await recordLanguage(language, langIndex);
        if (gen !== videoGenRef.current) {
          return;
        }
        finished.push({ language, blob });
      }
      setExportProgress(100);
      // Save only once both MP4s are fully encoded (share sheet → Camera Roll on mobile).
      await saveFiles(
        finished.map(({ language, blob }) => ({
          blob,
          filename: buildExportFilename(masterVmi, language, "mp4"),
        }))
      );
    } finally {
      spanish.value =
        langMode === "spanish" ? true : langMode === "english" ? false : previousSpanish;
      if (langMode === "both") {
        spanish.value = false;
      }
      setVideoPhase("done");
      setVideoBusy(false);
      setPreviewMode(true);
      setExportProgress(null);
    }
  };

  const handleExport = async () => {
    setExportError(null);
    setExporting(true);
    try {
      if (mediaMode === "video") {
        await handleExportVideo();
      } else {
        await handleExportPng();
      }
    } catch (error) {
      setExportError(
        error instanceof Error
          ? error.message
          : mediaMode === "video"
            ? "Could not export video."
            : "Could not export PNG."
      );
      setVideoBusy(false);
      if (mediaMode === "video") {
        setPreviewMode(true);
      }
      setExportProgress(null);
    } finally {
      setExporting(false);
      if (mediaMode === "video") {
        setExportProgress(null);
      }
    }
  };

  const showBoth = langMode === "both";
  const controlVmi = masterTick > 0 ? masterRef.current?.viewModelInstance ?? null : null;
  const gizmoAllowed = !showBoth && !framingActive;
  const sourceMode = mediaMode === "map" ? "map" : "file";

  const onMasterVideoLoadedMetadata = useCallback((video: HTMLVideoElement) => {
    const duration = video.duration;
    if (!Number.isFinite(duration) || duration <= 0) {
      return;
    }
    setVideoDuration(duration);
    setTrimStart(0);
    setTrimEnd(duration);
    setPlayheadTime(0);
  }, []);

  // Keep the Spanish duplicate clip aligned when Both view mounts or seeks.
  useEffect(() => {
    if (!showBoth || !videoActive) {
      return;
    }
    const time = playheadTime ?? trimStart;
    const secondary = secondaryMainVideoRef.current;
    if (!secondary) {
      return;
    }
    try {
      secondary.currentTime = time;
    } catch {
      /* ignore */
    }
  }, [playheadTime, showBoth, trimStart, videoActive, secondaryTick]);

  return (
    <div className="page-shell graphics-gen">
      {exportProgress != null ? (
        <div
          className="graphics-gen__export-progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={exportProgress}
          aria-label="Video export progress"
        >
          <div
            className="graphics-gen__export-progress-bar"
            style={{ width: `${exportProgress}%` }}
          />
          <span className="graphics-gen__export-progress-label">
            Exporting videos… {exportProgress}%
          </span>
        </div>
      ) : null}
      <PageHeader title="GRAPHICS GENERATOR" setPage={setPage} hue={200} />
      <div className="graphics-gen__layout">
        <div className="graphics-gen__stage">
          <SegmentedToggle
            ariaLabel="Language preview"
            value={langMode}
            onChange={setLangMode}
            options={[
              { id: "english", label: "English" },
              { id: "spanish", label: "Spanish" },
              { id: "both", label: "Both" },
            ]}
          />

          <div
            className={`graphics-gen__preview-stack${
              framingActive ? " is-framing" : ""
            }${videoActive ? " is-video" : ""}`}
          >
            <MapUnderlay
              active={framingActive}
              searchQuery={mapSearchQuery}
              selectedPlace={selectedPlace}
              onStatus={setMapStatus}
              mapRef={mapRef}
            />
            <div className={`graphics-gen__panes${showBoth ? " is-both" : ""}`}>
              <RivePane
                key="master"
                spanish={langMode === "spanish"}
                gizmoEnabled={gizmoAllowed}
                gizmoActive={gizmoActive}
                setGizmoActive={setGizmoActive}
                onReady={onMasterReady}
                label={showBoth ? "English" : undefined}
                framing={framingActive}
                videoUnderlay={videoActive}
                videoUrl={videoUrl}
                videoPhase={videoPhase}
                mainVideoRef={mainVideoRef}
                bumperVideoRef={bumperVideoRef}
                onMainLoadedMetadata={onMasterVideoLoadedMetadata}
                onMainTimeUpdate={onMainVideoTimeUpdate}
                onMainEnded={() => {
                  void handleMainVideoEnded();
                }}
                onBumperEnded={handleBumperEnded}
              />
              {showBoth ? (
                <RivePane
                  key="secondary-es"
                  spanish
                  gizmoEnabled={false}
                  gizmoActive={false}
                  setGizmoActive={() => undefined}
                  onReady={onSecondaryReady}
                  label="Spanish"
                  framing={framingActive}
                  videoUnderlay={videoActive}
                  videoUrl={videoUrl}
                  videoPhase={videoPhase}
                  mainVideoRef={secondaryMainVideoRef}
                  bumperVideoRef={secondaryBumperVideoRef}
                  videoMuted
                />
              ) : null}
            </div>
          </div>

          <p className="graphics-gen__gizmo-hint">
            {framingActive
              ? "Map framing: pan/zoom the map behind the graphic, then hit Confirm map frame."
              : videoActive
                ? showBoth
                  ? "Both view duplicates the clip under each language. Switch to English/Spanish to move/scale framing."
                  : "Click the graphic to move/scale the video. Intro fires at trim start, outro 0.5s before trim end, then the bumper."
                : showBoth
                  ? "Gizmo is hidden in Both view. Switch to English or Spanish to move/scale the image."
                  : "Click the graphic to show the move/scale gizmo. Click outside to hide it."}
          </p>
        </div>

        <aside className="graphics-gen__controls">
          <section className="graphics-gen__section">
            <h2>Media</h2>
            <SegmentedToggle
              ariaLabel="Media source"
              value={sourceMode}
              onChange={handleMediaModeChange}
              options={[
                { id: "file", label: "Photo / video" },
                { id: "map", label: "Use map" },
              ]}
            />

            {sourceMode === "file" ? (
              <>
                <div
                  className={`graphics-gen__dropzone${isDragging ? " is-dragging" : ""}`}
                  onDragEnter={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={(event) => {
                    event.preventDefault();
                    setIsDragging(false);
                  }}
                  onDrop={onDrop}
                  onClick={() => fileInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      fileInputRef.current?.click();
                    }
                  }}
                >
                  <p>
                    {videoName
                      ? `Video: ${videoName}`
                      : imageName
                        ? `Photo: ${imageName}`
                        : "Drop a photo or video, or click to upload"}
                  </p>
                  <p className="graphics-gen__dropzone-hint">
                    Photos replace graphic_db_img · videos enable video_mode_db_bool
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    className="graphics-gen__file-input"
                    onChange={onFileInputChange}
                  />
                </div>
                {mediaMode === "video" && videoUrl ? (
                  <>
                    {videoDuration > 0 ? (
                      <VideoFilmstripTrimmer
                        videoUrl={videoUrl}
                        duration={videoDuration}
                        trimStart={trimStart}
                        trimEnd={trimEnd}
                        playbackTime={playheadTime}
                        disabled={videoBusy || exporting}
                        onChangeStart={(next) => {
                          setTrimStart(
                            Math.min(
                              Math.max(0, next),
                              trimEnd - TRIM_HANDLE_MIN_GAP
                            )
                          );
                        }}
                        onChangeEnd={(next) => {
                          setTrimEnd(
                            Math.max(
                              Math.min(next, videoDuration),
                              trimStart + TRIM_HANDLE_MIN_GAP
                            )
                          );
                        }}
                        onScrub={(time) => {
                          if (videoBusy) {
                            return;
                          }
                          // Keep bumper hidden and show the raw clip under Rive.
                          if (videoPhase === "bumper") {
                            setVideoPhase("idle");
                            forEachBumperVideo((video) => video.pause());
                          }
                          setPlayheadTime(time);
                          forEachMainVideo((video) => {
                            try {
                              video.pause();
                              video.currentTime = time;
                            } catch {
                              /* ignore seek errors */
                            }
                          });
                        }}
                      />
                    ) : null}
                    <div className="graphics-gen__video-controls">
                      <button
                        type="button"
                        className={`graphics-gen__export${
                          videoBusy ? " is-stop" : ""
                        }`}
                        onClick={toggleVideoPreview}
                        disabled={exporting}
                      >
                        {videoBusy ? "STOP" : "Play preview"}
                      </button>
                    </div>
                  </>
                ) : null}
              </>
            ) : (
              <div className="graphics-gen__map-controls">
                <div className="graphics-gen__field" ref={locationFieldRef}>
                  <span>Location</span>
                  <div className="graphics-gen__map-search-row">
                    <input
                      type="text"
                      value={locationDraft}
                      placeholder="Address or place"
                      autoComplete="off"
                      onChange={(event) => {
                        setLocationDraft(event.target.value);
                        setSelectedPlace(null);
                        setSuggestionsOpen(true);
                      }}
                      onFocus={() => {
                        if (placeSuggestions.length) {
                          setSuggestionsOpen(true);
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          if (placeSuggestions[0]) {
                            handleSuggestionSelect(placeSuggestions[0]);
                          } else {
                            handleMapSearch();
                          }
                        } else if (event.key === "Escape") {
                          setSuggestionsOpen(false);
                        }
                      }}
                    />
                    <button type="button" onClick={handleMapSearch}>
                      Go
                    </button>
                  </div>
                  {suggestionsOpen && placeSuggestions.length > 0 ? (
                    <ul className="graphics-gen__suggestions" role="listbox">
                      {placeSuggestions.map((suggestion) => (
                        <li key={suggestion.id}>
                          <button
                            type="button"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => handleSuggestionSelect(suggestion)}
                          >
                            {suggestion.description}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                {mapFraming ? (
                  <button
                    type="button"
                    className="graphics-gen__export"
                    onClick={handleConfirmMap}
                    disabled={confirmingMap || !mapsConfigured}
                  >
                    {confirmingMap ? "Capturing…" : "Confirm map frame"}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="graphics-gen__map-reframe"
                    onClick={() => {
                      clearGraphicImage();
                      setMapFraming(true);
                      setGizmoActive(false);
                    }}
                    disabled={!mapsConfigured}
                  >
                    Reframe map
                  </button>
                )}
                {imageName ? (
                  <p className="graphics-gen__dropzone-hint">Current graphic: {imageName}</p>
                ) : null}
                {mapStatus ? <p className="graphics-gen__error">{mapStatus}</p> : null}
              </div>
            )}
          </section>

          <section className="graphics-gen__section">
            <h2>Date & time</h2>
            <BoundDateField viewModelInstance={controlVmi} />
            <BoundTimeField viewModelInstance={controlVmi} />
          </section>

          <section className="graphics-gen__section">
            <h2>Details</h2>
            <BoundDetainedField viewModelInstance={controlVmi} />
          </section>

          <section className="graphics-gen__section">
            <h2>Text</h2>
            {STRING_FIELDS.map((field) => (
              <BoundStringField
                key={field.path}
                path={field.path}
                label={field.label}
                viewModelInstance={controlVmi}
              />
            ))}
          </section>

          <div className="graphics-gen__export-row">
            <button
              type="button"
              className="graphics-gen__export"
              onClick={() => {
                void handleExport();
              }}
              disabled={exporting || (mediaMode === "video" && !videoUrl)}
            >
              {exporting
                ? "Exporting…"
                : mediaMode === "video"
                  ? "Export video"
                  : "Export graphic"}
            </button>
            <p className="graphics-gen__dropzone-hint">
              {mediaMode === "video"
                ? "Exports English and Spanish MP4s (trim + overlay + bumper)"
                : "Exports English and Spanish PNGs"}
            </p>
            {exportError ? <p className="graphics-gen__error">{exportError}</p> : null}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default function GraphicsGenerator(
  _timer: number,
  setPage: pagesetter,
  _mouse: Point,
  _extravars: reactvar[],
  _viewport: Viewport
) {
  return <GraphicsGeneratorPage setPage={setPage} />;
}
